import 'react-medium-image-zoom/dist/styles.css'

import Image from 'next/image'
import { cloneElement, FC, useState } from 'react'
import Zoom from 'react-medium-image-zoom'

import { FileApiType } from '@/api/shared/types'
import { cn } from '@/app/utils'
import {
  downloadFileByLink,
  getFileExtensionByPath,
  isFileImageByPath
} from '@/utils/file'

import { useRemoteFileSize } from '../hooks/useRemoteFileSize'
import { DownloadedFileIcon } from './DownloadedFileIcon'

type Props = {
  file: FileApiType
  inline?: boolean
  className?: string
  imgSizeClassName?: string
  contentClassName?: string
}

const FileUploadedCard: FC<Props> = ({
  file,
  inline,
  imgSizeClassName,
  contentClassName,
  className
}) => {
  const [isImageError, setIsImageError] = useState(false)

  const isImage = isFileImageByPath(file.name)
  const fileExtension = getFileExtensionByPath(file.name)
  const showImage = isImage && !isImageError

  const fileSizeWithUnit = useRemoteFileSize(showImage ? undefined : file.link)

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-1',
        !inline && 'min-h-[120px] xxl:min-h-[110px] xl:min-h-[96px]',
        className,
        showImage && imgSizeClassName
      )}
    >
      {showImage ? (
        <div className="h-full [&_div]:h-full">
          <Zoom
            ZoomContent={({ img, buttonUnzoom }) => (
              <>
                {img &&
                  cloneElement(img, {
                    'data-testid': 'image-zoom-modal-img-id'
                  })}
                {buttonUnzoom}
              </>
            )}
          >
            <Image
              width="0"
              height="0"
              sizes="100vw"
              className="block h-full min-h-[120px] w-full object-cover"
              src={file.link}
              alt={file.name}
              data-testid="file-uploaded-card-image-id"
              onError={() => setIsImageError(true)}
            />
          </Zoom>
        </div>
      ) : (
        <button
          type="button"
          className={cn(
            'group flex w-full cursor-pointer justify-between text-left',
            inline ? 'gap-10' : 'flex-col space-y-10'
          )}
          data-testid="file-uploaded-card-button-id"
          onClick={() => downloadFileByLink(file.name, file.link)}
        >
          <div className="flex h-48 w-48 items-center justify-center rounded-full bg-white">
            <div className="flex h-28 w-28 items-center justify-center">
              <DownloadedFileIcon fileName={file.name} />
            </div>
          </div>

          <div
            className={cn(
              'relative',
              inline ? 'max-w-[calc(100%-48px-10px)]' : 'w-full',
              contentClassName
            )}
          >
            <div className="w-full truncate text-center font-pro transition-opacity text-16">
              {file.name || 'Файл'}
            </div>
            <div className="mt-2 uppercase text-12">
              {fileExtension}
              {fileSizeWithUnit && ` • ${fileSizeWithUnit}`}
            </div>
          </div>
        </button>
      )}
    </div>
  )
}

export default FileUploadedCard
